import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  SegmentedControl,
  Text,
  TextInput,
} from '@mantine/core';
import { Editor } from '@monaco-editor/react';
import {
  IconCode,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceMobileMessage,
  IconRotate,
} from '@tabler/icons-react';
import { useLocalStorage } from '@mantine/hooks';
import { useState } from 'react';
import { DEFAULT_CHROME_CSS_STYLES, EMPTY_CHROME_CSS_STYLES, MODAL_DISPLAY_TYPES } from '../constants/bannerChrome';
import { countCssDeclarations } from '../helpers/cssStatements';
import classes from './PageBlockAppearance.module.css';
import InsertStrategyPicker from './InsertStrategyPicker';
import PageBlockAdvancedStyling from './PageBlockAdvancedStyling';
import PageBlockPreview from './PageBlockPreview';
import PreviewDeviceFrame from './PreviewDeviceFrame';
import ConfirmationModal from './Modals/ConfirmationModal';

// The one section of this form that folds away, named once so the control and
// the state that opens it cannot drift apart.
const CUSTOM_CSS_SECTION = 'customCss';

// Where the author's choice of preview device is remembered. Namespaced
// because it is shared with every other admin tab on this origin.
const PREVIEW_DEVICE_KEY = 'releva-preview-device';

// The devices the switch offers. `mobile` is a phone BROWSER, running the web
// SDK at a phone's viewport width; `app` is the same phone showing what the
// native app SDKs draw, which differs on purpose — they honour five of the
// eleven close-button keys and place the control inside the card. A page
// carries no web/app flag, so a block can be served to both and this is a
// third device rather than a replacement for the second.
const DEVICES = ['desktop', 'mobile', 'app'];

const APP_PREVIEW_CAPTION =
  "As drawn by the iOS and Android app SDKs: the content comes from the editor's design, not from the banner's " +
  'HTML, and rich text may be flattened to plain text. Close-button symbol, padding, font weight, line height and ' +
  'top/right position are web only and are ignored here.';

// How the block looks: the chrome the SDK draws around it, and the preview the
// author watches while they style it. Its sibling,
// `PageBlockDisplayConfiguration`, answers the other question — when and
// whether the block is shown at all. Display mode stays over there even though
// it decides whether this is a pop-up or a bar, because it is what decides
// which rules that tab offers in the first place.
const PageBlockAppearance = ({
  pageBlock,
  handleChange,
  preview,
  // A preview that knows which device it is being asked for, as a function of
  // one. Separate from `preview` rather than polymorphic with it: a caller
  // either renders at the device's own viewport width or it does not, and the
  // device switch is only offered to the ones that do. The recommender form
  // passes a plain node (its preview is a rendered layout, not an SDK render
  // against a viewport) and gets no switch.
  renderPreview,
  // Shown in the desktop frame's address bar.
  previewUrl,
  // What the app SDKs drop from this block's content, as a sentence, or
  // nothing when they draw all of it. Passed in because it is read off the
  // banner's design, which the owner form holds and this shared form does not.
  appContentNote,
  // Wraps the CSS selector field in the control that places a static block on
  // a page of the customer's own site. Passed in rather than rendered here
  // because the placement session it needs is addressed per block type, which
  // the caller knows and this shared form does not; a caller that passes
  // nothing gets the selector field on its own.
  positionOnSite = (cssSelectorField) => cssSelectorField,
  // Whether the modal a pop-up, bar or flyout displays in is drawn around the
  // preview here. A preview that renders the SDK's own modal passes false, so
  // the merchant sees one of them rather than two.
  withChrome = true,
  // Whether `displayPosition` is a column the block's own API persists.
  // Banner blocks pass true; `recommenders` never had the column and the
  // recommender API's `allowedFields` omits it, so a write to it there is
  // dropped on save. Both halves of the sync below hang off this.
  displayPositionPersisted = false,
}) => {
  // The Custom CSS editor is folded away until it is asked for: it is a
  // last-resort control that most blocks never use, and unfolded it pushed
  // every styling control the author came for above the fold. Controlled
  // rather than left to the Accordion, because the editor is only mounted
  // while the section is open — Monaco measures itself on mount, and one
  // mounted inside a collapsed panel comes up sized to nothing.
  const [customCssOpen, setCustomCssOpen] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  // Remembered across blocks and sessions: which device an author previews on
  // is a fact about them, not about the block, and re-picking Mobile on every
  // block is the kind of small tax that makes a preview feel like a chore.
  //
  // `getInitialValueInEffect: false` so the stored value is read during the
  // first render rather than swapped in by an effect — this is a client-only
  // app, and the default would show a Mobile author a frame of Desktop and a
  // re-render of the frame around their block. Mantine's hook wraps every
  // storage access in try/catch, so a browser with storage blocked gets the
  // default and a working tab rather than an exception, and `sync` keeps two
  // admin tabs from disagreeing.
  const [storedDevice, setStoredDevice] = useLocalStorage({
    key: PREVIEW_DEVICE_KEY,
    defaultValue: 'desktop',
    getInitialValueInEffect: false,
  });

  // Anything outside the three is desktop: a key this readable is a key
  // someone will hand-edit, and a stored typo must not leave the preview
  // rendering against nothing.
  const device = DEVICES.includes(storedDevice) ? storedDevice : 'desktop';

  const customCssDeclarations = countCssDeclarations(pageBlock.cssStyles?.customCss);

  // `static` and `custom` blocks are inserted into the page rather than
  // wrapped in a card the SDK draws, so there is no chrome to style and this
  // tab is the preview alone.
  const hasChrome = !['static', 'custom'].includes(pageBlock.displayType);

  // The other half of the switch this tab replaced. Its "on" position is
  // derived now — styling anything turns `advancedStyling` on, because the API
  // serves a `false` block the documented defaults and drops its stored
  // `cssStyles` — but "off" had no replacement at all, which made the flag a
  // one-way door: every route back to the standard look meant resetting some
  // twenty fields across three sections by hand.
  //
  // It comes back as an action rather than a mode, and it writes both halves:
  // the seed `cssStyles` for this display type *and* `advancedStyling: false`,
  // which together leave the block in exactly the state a newly created one is
  // in. Writing only the flag would reproduce the switch's own flaw — the shop
  // would render defaults while this panel went on showing the values it no
  // longer applies, with nothing on screen to say so.
  const resetToStandard = (close) => {
    handleChange({
      target: {
        name: 'cssStyles',
        value: {
          ...(MODAL_DISPLAY_TYPES.includes(pageBlock.displayType)
            ? DEFAULT_CHROME_CSS_STYLES
            : EMPTY_CHROME_CSS_STYLES),
        },
      },
    });
    handleChange({ target: { name: 'advancedStyling', value: false } });
    close();
  };

  return (
    // Two `overflow: visible` overrides, both load-bearing for the sticky
    // preview column below and neither optional: an ancestor with
    // `overflow: hidden` becomes the scrollport a sticky descendant sticks
    // inside, and since neither of these scrolls, the preview simply never
    // sticks. `Card`'s comes from Mantine's own stylesheet; the `Grid`'s is
    // set globally by this app's theme (`theme.jsx` → `Grid.styles.root`),
    // which is an inline style, so only the `styles` prop can outrank it —
    // the documented `overflow="visible"` prop only sets a CSS variable the
    // inline style then beats. Measured in a real browser, not inferred:
    // jsdom computes no layout, so every test passes either way.
    <Card withBorder styles={{ root: { overflow: 'visible' } }}>
      <Card.Section p="md">
        <Grid styles={{ root: { overflow: 'visible' } }}>
          <Grid.Col span={{ base: 12, md: 7 }}>
            {/* Where a static block attaches to the shop's own DOM. It lives on
            this tab because it is the same question the placement grid answers
            for the modes the SDK draws a card for — where the block goes —
            and because without it this tab is nothing but a preview for the
            one display type that has no chrome to style. */}
            {pageBlock.displayType === 'static' && (
              <>
                <Grid mb="md" data-tour="cssSelectorField">
                  <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <label>CSS Selector</label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    {positionOnSite(
                      <TextInput
                        flex="1"
                        name="cssSelector"
                        value={pageBlock.cssSelector || ''}
                        placeholder="e.g. body > #main"
                        onChange={handleChange}
                        error={
                          !pageBlock.cssSelector && pageBlock.templateId
                            ? 'CSS selector is required when using a template.'
                            : null
                        }
                      />
                    )}
                    <Text size="xs" c="dimmed" mt="xs">
                      Use this field to refine where your integration will display this product block. If you leave this
                      blank, the product block will be displayed at a location determined by the callback in your
                      frontend integration.
                    </Text>
                  </Grid.Col>
                </Grid>
                <Grid mb="md" data-tour="strategyField">
                  <Grid.Col span={{ base: 12, sm: 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <label>Insert strategy</label>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <InsertStrategyPicker
                      value={pageBlock.displayStrategy ?? ''}
                      onChange={(value) => handleChange({ target: { name: 'displayStrategy', value } })}
                    />
                  </Grid.Col>
                </Grid>
              </>
            )}

            {/* No switch in front of it any more. "Advanced Pop-up styling" was a
            toggle that revealed this panel, from when the panel shared a tab
            with the display rules; on a tab whose whole subject is how the
            block looks, it was a door in front of the only room. The
            `advancedStyling` column it wrote still exists and still matters —
            the API serves `false` the documented defaults and drops the
            stored `cssStyles` — so the owner forms now set it from the act of
            styling instead (see `BannerBlockForm.handleStateChange`). */}
            {hasChrome && (
              <PageBlockAdvancedStyling
                pageBlock={pageBlock}
                displayPositionPersisted={displayPositionPersisted}
                // Forwarded straight to the owner form's own handler,
                // not pre-merged here: the card placement picker can
                // fire two `cssStyles` writes for one gesture (one per
                // axis, for a pop-up or a flyout), batched before any
                // re-render. Pre-merging against `pageBlock.cssStyles`
                // from this render's closure computed both writes from
                // the same pre-gesture object, so the second clobbered
                // the first. `handleChange` (`BannerBlockForm` and
                // `RecommenderForm`) merges chrome keys with a
                // functional state updater instead, which reads from
                // `previous` and so sees the first write too.
                handleStateChange={handleChange}
              />
            )}

            {hasChrome && (
              <Box mt="md">
                <Accordion
                  variant="contained"
                  value={customCssOpen ? CUSTOM_CSS_SECTION : null}
                  onChange={(value) => setCustomCssOpen(value === CUSTOM_CSS_SECTION)}
                >
                  <Accordion.Item value={CUSTOM_CSS_SECTION}>
                    <Accordion.Control icon={<IconCode size={16} />}>
                      <Group gap="xs">
                        <span>Custom css</span>
                        {/* What is in there, for a section that is closed. A
                          block nobody has styled gets no badge at all rather
                          than a zero, which would read as a thing to go and
                          look at. */}
                        {customCssDeclarations > 0 && (
                          <Badge size="sm" variant="light" color="gray">
                            {customCssDeclarations} {customCssDeclarations === 1 ? 'declaration' : 'declarations'}
                          </Badge>
                        )}
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {customCssOpen && (
                        <Editor
                          defaultLanguage="css"
                          style={{ width: '100%' }}
                          onChange={(value) => {
                            handleChange({ target: { name: 'customCss', value } });
                          }}
                          name="customCss"
                          height="100pt"
                          value={pageBlock.cssStyles?.customCss ?? ''}
                        />
                      )}
                      <Text size="xs" c="dimmed" mt="xs">
                        Enter your custom CSS code in this section.
                        {pageBlock.token && (
                          <>
                            {' '}
                            To specifically target this template, you can utilize the ID selector{' '}
                            <b>#rlv-banner-{pageBlock.token}-modal</b>
                          </>
                        )}
                      </Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Box>
            )}
            {hasChrome && (
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  size="compact-sm"
                  color="gray"
                  leftSection={<IconRotate size={14} />}
                  onClick={() => setConfirmResetOpen(true)}
                >
                  Reset to the standard look
                </Button>
              </Group>
            )}
          </Grid.Col>

          {/* Beside the controls rather than under them, and sticky: styling is
              a change-and-look loop, and a preview below a column of fieldsets
              is one the author scrolls away from to make the very change they
              want to see. For a `static` or `custom` block — which has no
              chrome to style — the controls column is empty and this is the
              whole tab.

              `usePreview` (the recommender preview) returns the empty string,
            not a spinner, for "nothing to load" — a `static` product block
            with no layout chosen yet, or one whose layout was deleted out
            from under it. Left to `PageBlockPreview`, that renders a bordered
            card with nothing in it, which for a `static` block *is* the whole
            tab — reading as a hung page rather than a block that is simply
            not styled yet. `SdkPreview` (the banner preview) never returns
              this — it always renders a frame — so the message below can only
              show for the recommender case it is written for. */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <div className={classes.preview}>
              {renderPreview && (
                <SegmentedControl
                  fullWidth
                  size="xs"
                  mb="xs"
                  value={device}
                  onChange={setStoredDevice}
                  data={[
                    {
                      value: 'desktop',
                      label: (
                        <Center style={{ gap: 6 }}>
                          <IconDeviceDesktop size={14} />
                          <span>Desktop</span>
                        </Center>
                      ),
                    },
                    {
                      value: 'mobile',
                      label: (
                        <Center style={{ gap: 6 }}>
                          <IconDeviceMobile size={14} />
                          <span>Mobile</span>
                        </Center>
                      ),
                    },
                    {
                      value: 'app',
                      label: (
                        <Center style={{ gap: 6 }}>
                          <IconDeviceMobileMessage size={14} />
                          <span>App</span>
                        </Center>
                      ),
                    },
                  ]}
                />
              )}
              {preview === '' ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Choose a layout on the Display Configuration tab to see a preview here.
                </Text>
              ) : renderPreview ? (
                <>
                  <PreviewDeviceFrame device={device} url={previewUrl}>
                    <PageBlockPreview block={pageBlock} preview={renderPreview(device)} withChrome={withChrome} />
                  </PreviewDeviceFrame>
                  {/* Only for a block the SDKs draw a card for: a static or
                      custom block is inserted into the screen on every
                      platform, so App is Mobile for it and there is no
                      difference to explain. */}
                  {device === 'app' && hasChrome && (
                    <>
                      <Text size="xs" c="dimmed" mt="xs">
                        {APP_PREVIEW_CAPTION}
                      </Text>
                      {appContentNote && (
                        <Text size="xs" c="dimmed" mt="xs">
                          {appContentNote}
                        </Text>
                      )}
                    </>
                  )}
                </>
              ) : (
                <PageBlockPreview block={pageBlock} preview={preview} withChrome={withChrome} />
              )}
            </div>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <ConfirmationModal
        show={confirmResetOpen}
        close={() => setConfirmResetOpen(false)}
        confirm={resetToStandard}
        message="This puts the card, its placement and the close button back to the standard look, and discards the styling set here. Custom css is cleared too. The block's content is untouched."
      />
    </Card>
  );
};

export default PageBlockAppearance;
